import type { OrderStatus, Plan } from '../types'

const configuredBase = import.meta.env.VITE_API_URL?.trim().replace(/\/$/, '') ?? ''

export class ApiError extends Error {
  readonly code: string
  readonly status: number

  constructor(message: string, code = 'REQUEST_FAILED', status = 500) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
  }
}

interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  token?: string | null
  json?: unknown
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const headers = new Headers(options.headers)
  if (options.json !== undefined) headers.set('Content-Type', 'application/json')
  if (options.token) headers.set('Authorization', `Bearer ${options.token}`)

  let response: Response
  try {
    response = await fetch(`${configuredBase}${path}`, {
      ...options,
      headers,
      body: options.json === undefined ? undefined : JSON.stringify(options.json),
    })
  } catch {
    throw new ApiError('Could not connect to CheatsEclipse.', 'NETWORK_ERROR', 0)
  }

  const payload = (await response.json().catch(() => ({}))) as {
    error?: { code?: string; message?: string }
  }

  if (!response.ok) {
    throw new ApiError(
      payload.error?.message ?? 'The request could not be completed.',
      payload.error?.code ?? 'REQUEST_FAILED',
      response.status,
    )
  }

  return payload as T
}

export function formatMoney(cents: number, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(cents / 100)
}

export function formatDate(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value))
}

// Product art can be any of these extensions; ProductImage tries them in order.
export const PRODUCT_IMAGE_EXTS = ['svg', 'png', 'webp', 'jpg'] as const

export function productImage(productName: string, ext: string = PRODUCT_IMAGE_EXTS[0]) {
  return `/products/${productName}.${ext}`
}

export function planPrice(product: { pricing: { weekly: number; monthly: number; lifetime: number } }, plan: Plan) {
  return product.pricing[plan]
}

export const planLabels: Record<Plan, string> = {
  weekly: '1-week license',
  monthly: '1-month license',
  lifetime: 'Lifetime license',
}

export const planShortLabels: Record<Plan, string> = {
  weekly: '1 week',
  monthly: '1 month',
  lifetime: 'Lifetime',
}

export const orderStatusLabels: Record<OrderStatus, string> = {
  pending: 'Pending',
  active: 'Active',
  paid: 'Paid',
  past_due: 'Past due',
  paused: 'Paused',
  canceled: 'Canceled',
  expired: 'Expired',
  failed: 'Failed',
}
